'use client';

import { useIntersectionTracking } from 'providers/OptimizelyProvider';
import { Suspense, useEffect } from 'react';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { useUser } from '@/app_lib/providers/UserProvider';
import { GoogleTagManager } from '@/app_lib/services/Gtm';
import type { PageAdConfig } from '@/app_lib/types/Ad';
import type { ArcSpanPageConfig } from '@/app_lib/types/ArcSpan';
import type { PageViewDataLayerObject } from '@/app_lib/types/Gtm';
import type { PianoPageConfig } from '@/app_lib/types/accounts/piano/Piano';

/**
 * ArticleWrapper component sets up client-sided page-level configurations.
 *
 * @param children - React children nodes to be rendered inside the wrapper.
 * @param pageConfig - Configuration object for Piano page settings.
 * @param arcSpanConfig - Configuration object for ArcSpan page settings.
 * @param pageAdConfig - Configuration object for page advertisement settings.
 * @param url - The canonical URL of the article, used for tracking.
 *
 * @remarks
 * - Initializes Piano, ArcSpan, and Ad context configurations on mount.
 * - Tracks optimizely events.
 * - Renders a paywall component after the children.
 * - For some reason, if we pass in the article itself we get some oddities with the byline which uses radix and the article.
 *   I have no clue why this is happening so avoid passing in the article itself for now.
 */
export const SectionWrapper = ({
  children,
  pageConfig,
  arcSpanConfig,
  pageAdConfig,
  url,
  dataLayer,
}: {
  children?: React.ReactNode;
  pageConfig: PianoPageConfig;
  arcSpanConfig: ArcSpanPageConfig;
  pageAdConfig: PageAdConfig;
  url: string;
  dataLayer: PageViewDataLayerObject;
}) => {
  const { setPianoPageConfig } = useUser();
  const { setPageAdConfig, setArcSpanPageConfig } = useAdContext();

  useEffect(() => {
    setPianoPageConfig(pageConfig);
    setArcSpanPageConfig(arcSpanConfig);
    setPageAdConfig(pageAdConfig);
  }, [setPianoPageConfig, pageConfig, arcSpanConfig, pageAdConfig, setPageAdConfig, setArcSpanPageConfig]);

  // biome-ignore-start lint/style/useNamingConvention: <external data>
  // TODO: Should we be tracking these ads?
  useIntersectionTracking('Leaderboard0', { URL: url, value: 1 }, 'leaderboard_ad');
  // useIntersectionTracking('fortunecom_incontent0', { URL: url, value: 2 }, 'saw_first_ad');
  // useIntersectionTracking('fortunecom_incontent1', { URL: url, value: 2 }, 'saw_second_ad');
  // useIntersectionTracking('fortunecom_incontent2', { URL: url, value: 2 }, 'saw_third_ad');
  // useIntersectionTracking('fortunecom_incontent3', { URL: url, value: 2 }, 'saw_fourth_ad');
  // useIntersectionTracking('fortunecom_incontent4', { URL: url, value: 2 }, 'saw_fifth_ad');
  // biome-ignore-end lint/style/useNamingConvention: <>

  return (
    <div className='section-page-wrapper container-ft'>
      <Suspense fallback={null}>
        <GoogleTagManager dataLayer={dataLayer} />
      </Suspense>
      {children}
    </div>
  );
};
